import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  CHART_PALETTE,
  fetchOperations,
  formatDays,
  formatPercent,
} from "../../lib/reports";
import { downloadCsv, reportFilename } from "../../lib/csv";
import ExportCsvButton from "./ExportCsvButton";

function Skeleton() {
  return <div className="h-48 animate-pulse rounded bg-slate-100" />;
}

function NoData() {
  return (
    <div className="flex h-40 items-center justify-center rounded border border-dashed border-slate-200 text-sm text-slate-500">
      No data for this period
    </div>
  );
}

function StatCard({ label, value, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-md border border-slate-200 bg-white p-4 shadow-card"
    >
      <div className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-500">
        {label}
      </div>
      <div className="font-heading text-2xl font-bold text-slate-900 tabular-nums mt-1.5">
        {value}
      </div>
    </div>
  );
}

function Section({ title, subtitle, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="rounded-md border border-slate-200 bg-white shadow-card"
    >
      <div className="border-b border-slate-200 px-4 py-3">
        <div className="font-heading text-sm font-semibold text-slate-900">
          {title}
        </div>
        {subtitle && (
          <div className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">
            {subtitle}
          </div>
        )}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

export default function OperationsTab({ params }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = () => {
    setLoading(true);
    setErr(null);
    fetchOperations(params)
      .then((d) => {
        setData(d);
        setLoading(false);
      })
      .catch((e) => {
        setErr(e?.response?.data?.detail || "Failed to load operations");
        setLoading(false);
      });
  };

  useEffect(load, [params.date_from, params.date_to]);

  if (loading) {
    return (
      <div className="grid gap-4 lg:grid-cols-2">
        <Skeleton />
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </div>
    );
  }
  if (err) {
    return (
      <div
        data-testid="ops-error"
        className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700"
      >
        {err}{" "}
        <button onClick={load} className="ml-3 underline" data-testid="ops-retry">
          Retry
        </button>
      </div>
    );
  }
  if (!data) return null;

  const exportCsv = () => {
    const rows = [];
    rows.push(["Counts", "SRs created", data.service_requests_created]);
    rows.push(["Counts", "WOs created", data.work_orders_created]);
    rows.push(["Counts", "WOs completed", data.work_orders_completed]);
    rows.push(["Counts", "Reports finalized", data.diagnostic_reports_finalized]);
    rows.push(["Counts", "Avg SR to WO (days)", data.avg_sr_to_wo_days ?? ""]);
    rows.push(["Counts", "Avg WO completion (days)", data.avg_wo_completion_days ?? ""]);
    rows.push(["Counts", "Payment requests sent", data.payment_requests_sent]);
    rows.push(["Counts", "Payment requests paid", data.payment_requests_paid]);
    rows.push(["Counts", "Payment conversion rate", data.payment_conversion_rate ?? ""]);
    (data.service_requests_by_status || []).forEach((r) =>
      rows.push(["SRsByStatus", r.status, r.count])
    );
    (data.service_requests_by_intake || []).forEach((r) =>
      rows.push(["SRsByIntake", r.intake_source, r.count])
    );
    (data.work_orders_by_status || []).forEach((r) =>
      rows.push(["WOsByStatus", r.status, r.count])
    );
    downloadCsv(
      reportFilename("operations", params),
      ["Section", "Key", "Value"],
      rows
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <ExportCsvButton onExport={exportCsv} testId="export-csv-operations" />
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard
          label="SRs created"
          value={data.service_requests_created}
          testId="ops-sr-created"
        />
        <StatCard
          label="WOs created"
          value={data.work_orders_created}
          testId="ops-wo-created"
        />
        <StatCard
          label="WOs completed"
          value={data.work_orders_completed}
          testId="ops-wo-completed"
        />
        <StatCard
          label="Reports finalized"
          value={data.diagnostic_reports_finalized}
          testId="ops-reports-finalized"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <StatCard
          label="Avg SR → WO"
          value={formatDays(data.avg_sr_to_wo_days)}
          testId="ops-avg-sr-wo"
        />
        <StatCard
          label="Avg WO completion"
          value={formatDays(data.avg_wo_completion_days)}
          testId="ops-avg-wo-comp"
        />
        <StatCard
          label="Payment conversion"
          value={formatPercent(data.payment_conversion_rate)}
          testId="ops-payment-conv"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section
          title="Service requests by intake"
          subtitle="how tickets came in"
          testId="ops-by-intake"
        >
          {(data.service_requests_by_intake || []).length === 0 ? (
            <NoData />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={data.service_requests_by_intake}
                margin={{ top: 5, right: 10, bottom: 5, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="intake_source" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {data.service_requests_by_intake.map((_, i) => (
                    <Cell key={i} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Section>

        <Section
          title="Service requests by status"
          subtitle="ticket pipeline"
          testId="ops-by-status"
        >
          {(data.service_requests_by_status || []).length === 0 ? (
            <NoData />
          ) : (
            <div className="flex flex-wrap gap-2">
              {data.service_requests_by_status.map((row) => (
                <div
                  key={row.status}
                  data-testid={`ops-sr-status-${row.status}`}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5"
                >
                  <span className="font-data text-[10px] uppercase tracking-wide text-slate-600">
                    {row.status.replace("_", " ")}
                  </span>
                  <span className="font-heading text-sm font-bold text-slate-900 tabular-nums">
                    {row.count}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
